import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { localStorageInstance } from "../../../utils/localStorage";
import { searchBoard } from "../../../redux/SearchSlice";
import { isAdmin } from "../../../utils/constants";
import { changeExpand } from "../../../redux/boardListSlice";
import { setSideBarDrawer } from "../../../redux/Sidebar";
import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { useResponsive } from "../../../hooks/useResponsive";

export function useProfileMenuState() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.users.logInUser);
  const isMobile = useResponsive("down", "sm");
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMergeBoard, setOpenMergeBoard] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [showFeedBackDialog, setShowFeedBackDialog] = useState(false);
  const [boardParam] = useCustomSearchParams("find-brd");

  useEffect(() => {
    dispatch(searchBoard(boardParam || ""));
    if (isAdmin() && boardParam) {
      dispatch(changeExpand("allPanel"));
    }
  }, [boardParam, dispatch]);

  const userName = useMemo(
    () =>
      Object.hasOwn(user, "first_name") &&
      `${user.first_name[0]}${user.last_name[0]}`.toUpperCase(),
    [user]
  );

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(() => {
    setIsLogoutLoading(true);
    if (sessionStorage.getItem("userId")) {
      window.close();
    } else {
      localStorageInstance.clear();
    }
    queryClient.clear();
    navigate("/");
    setIsLogoutLoading(false);
  }, [navigate, queryClient]);

  const handleNavigateToMissingStatus = useCallback(() => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const date = {
      startDate: yesterday,
      endDate: yesterday,
      key: "selection",
    };

    const newSearchParams = new URLSearchParams({
      tab: "worklog",
      board: "",
      epic: "null",
      missingStatus: JSON.stringify(date),
    });
    navigate(`/admin-report?${newSearchParams.toString()}`);
    setAnchorEl(null);
    dispatch(setSideBarDrawer(false));
  }, [dispatch, navigate]);

  const handleMenuClick = useCallback(
    (menuItem) => {
      switch (menuItem.id) {
        case "feedback":
          setShowFeedBackDialog(true);
          break;
        case "missingStatus":
          handleNavigateToMissingStatus();
          break;
        case "mergeBoard":
          setOpenMergeBoard(true);
          break;
        case "logout":
          setShowLogoutDialog(true);
          break;
        default:
          if (menuItem.path) {
            navigate(menuItem.path);
            dispatch(setSideBarDrawer(false));
          }
      }
      setAnchorEl(null);
    },
    [dispatch, handleNavigateToMissingStatus, navigate]
  );

  return {
    anchorEl,
    handleClick,
    handleClose,
    handleLogout,
    handleMenuClick,
    isLogoutLoading,
    isMobile,
    openMergeBoard,
    openMenu: Boolean(anchorEl),
    showFeedBackDialog,
    showLogoutDialog,
    user,
    userName,
    closeFeedBackDialog: () => setShowFeedBackDialog(false),
    closeLogoutDialog: () => setShowLogoutDialog(false),
    closeMergeBoard: () => setOpenMergeBoard(false),
  };
}
